/**
 * Utility functions for handling user profiles and profile pictures
 */

/**
 * Get the best available profile picture for a user
 * Priority: App uploaded > Google profile > null (will show initials)
 * @param {Object} user - User object (can be from profile, auth, or message)
 * @returns {string|null} Profile picture URL or null
 */
export const getProfilePicture = (user) => {
  if (!user) return null;
  
  // Priority 1: App uploaded profile picture
  if (user.profile_picture_url) {
    return user.profile_picture_url;
  }
  
  // Priority 2: Google account profile picture
  if (user.photoURL) {
    return user.photoURL;
  }
  
  // Priority 3: No picture available
  return null;
};

/**
 * Get user initials for fallback display
 * @param {Object} user - User object
 * @returns {string} User initials (max 2 characters)
 */
export const getUserInitials = (user) => {
  if (!user) return '?';
  
  const name = user.author_name || user.full_name || user.displayName || '';
  const email = user.email || '';
  
  if (name && name.trim()) {
    return name.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2);
  }
  
  if (email) {
    return email.charAt(0).toUpperCase();
  }
  
  return '?';
};

/**
 * Check if a profile picture URL is from Google services
 * @param {string} url - Profile picture URL
 * @returns {boolean} True if it's a Google profile picture
 */
export const isGoogleProfilePicture = (url) => {
  if (!url) return false;
  return url.includes('googleusercontent.com') || url.includes('googleapis.com');
};

/**
 * Get user role color class
 * @param {string} role - User role
 * @returns {string} CSS color class
 */
export const getRoleColor = (role) => {
  switch (role) {
    case 'admin': return 'text-red-400';
    case 'writer': return 'text-green-400';
    case 'qa': return 'text-blue-400';
    case 'client': return 'text-purple-400';
    default: return 'text-gray-400';
  }
};

/**
 * Get user role icon name
 * @param {string} role - User role
 * @returns {string} Icon name
 */
export const getRoleIcon = (role) => {
  switch (role) {
    case 'admin': return 'Shield';
    case 'writer': return 'PenTool';
    case 'qa': return 'CheckCircle';
    case 'client': return 'User';
    default: return 'User';
  }
};

/**
 * Extract user role from profile data
 * @param {Object} user - User object
 * @returns {string} User role
 */
export const getUserRole = (user) => {
  if (!user) return 'user';
  
  return user.author_role || user.role || 
    (user.is_admin ? 'admin' : 
     user.is_writer ? 'writer' : 
     user.is_qa ? 'qa' : 
     user.is_client ? 'client' : 'user');
};

export default {
  getProfilePicture,
  getUserInitials,
  isGoogleProfilePicture,
  getRoleColor,
  getRoleIcon,
  getUserRole
};